/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  FormControl,
} from "@mui/material";
import { CloudUploadOutlined } from "@mui/icons-material";
import Layout from "../../Components/Layout";
import { CRUDFormPageProps } from "../../types";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import dayjs, { Dayjs } from "dayjs";

import AxiosInstance from "../../API/axios";
import { BASE_API_URL } from "../../API/Base";
import toast from "react-hot-toast";

const CRUDForm: React.FC<CRUDFormPageProps> = ({ mode, data, fields }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [postImage, setPostImage] = useState<any>(null);
  const [postImageName, setPostImageName] = useState<any>(null);
  const [title, setTitle] = useState<string>("");
  const [date, setDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (mode === "edit") {
      setTitle(data.title);
      setDate(dayjs(data.published_date));
      setPostImageName(data.image);
    }
  }, [data, mode]);

  const base64Decode = (encodedStr: string) => {
    return atob(encodedStr);
  };

  const handleModuleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const imageFile = e.target.files?.[0];
    setPostImage(imageFile);
    setPostImageName(imageFile?.name || null);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (mode === "create") {
      // Create a FormData object
      const formData = new FormData();
      formData.append("title", title);
      formData.append("image", postImage);
      if (date) {
        formData.append("date", format(date.toDate(), "MM-dd-yyyy"));
      }

      try {
        const response = await AxiosInstance.post(
          `${BASE_API_URL}/admin/web-content/v1/latest-notification`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (response.status === 200) {
          toast.success("Post added successfully");
        } else {
          toast.error("Failed to add post. Please try again later.");
        }
      } catch (error) {
        console.log(error);
      }
    } else if (mode === "edit") {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("image", postImage);
      if (date) {
        formData.append("date", format(date.toDate(), "MM-dd-yyyy"));
      }

      try {
        const decodedId = id ? base64Decode(id) : null;

        const response = await AxiosInstance.put(
          `${BASE_API_URL}/admin/web-content/v1/latest-notification/${decodedId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.status === 200) {
          toast.success("Post updated successfully");
        } else {
          toast.error("Failed to update post. Please try again later.");
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  return (
    <Layout PageId={11}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <CircularProgress size={50} />
          </div>
        )}

        <div className="flex flex-col items-start justify-start w-full h-full mt-4">
          <Grid container spacing={2}>
            {mode === "create" && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Post title"
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <input
                    accept="image/*"
                    id="icon-button-file"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleModuleImageChange}
                  />
                  <TextField
                    fullWidth
                    label="Upload image"
                    value={postImageName || ""}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          color="primary"
                          aria-label="upload picture"
                          component="span"
                          onClick={() => {
                            const fileInput =
                              document.getElementById("icon-button-file");
                            if (fileInput) fileInput.click();
                          }}
                        >
                          <CloudUploadOutlined color="primary" />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="dense" size="small">
                    <DatePicker
                      label="Published Date"
                      value={date} // Value should be of type Dayjs or null
                      onChange={(selectedDate: Dayjs | null) => {
                        if (selectedDate) {
                          setDate(selectedDate);
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
            {mode === "edit" && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Post title"
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <input
                    accept="image/*"
                    id="icon-button-file"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleModuleImageChange}
                  />
                  <TextField
                    fullWidth
                    label="Upload image"
                    value={postImageName || ""}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          color="primary"
                          aria-label="upload picture"
                          component="span"
                          onClick={() => {
                            const fileInput =
                              document.getElementById("icon-button-file");
                            if (fileInput) fileInput.click();
                          }}
                        >
                          <CloudUploadOutlined color="primary" />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="dense" size="small">
                    <DatePicker
                      label="Published Date"
                      value={date} // Value should be of type Dayjs or null
                      onChange={(selectedDate: Dayjs | null) => {
                        if (selectedDate) {
                          setDate(selectedDate);
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
              </>
            )}

            {mode === "view" && (
              <>
                {fields.map(({ label, key }) => (
                  <Grid item xs={6} key={key}>
                    <TextField
                      label={label}
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      size="small"
                      value={data[key] || ""}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>

          <div className="flex flex-row items-center justify-between w-full mt-4">
            <Button
              variant="outlined"
              onClick={() => navigate("/web/latest-notifications/manage")}
            >
              Back
            </Button>
            {mode === "create" && (
              <Button
                variant="contained"
                sx={{ color: "white" }}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            )}
            {mode === "edit" && (
              <Button
                variant="contained"
                color="primary"
                sx={{ color: "white" }}
                onClick={handleSubmit}
              >
                Save Changes
              </Button>
            )}
          </div>
        </div>
      </LocalizationProvider>
    </Layout>
  );
};

export default CRUDForm;
